/**
 * AgentRejectModal — reject an agent membership request.
 *
 * Pulls the configured "agency_rejection" reasons from the admin dropdown
 * lists (managed in: Control Panel → CLOUDGM FZ LLC → التحكم والمعلومات
 * بمنصة التجار → القائمة المنسدلة لطلب المتاجر → أسباب رفض طلب الوكالة).
 *
 * The user picks one reason from the dropdown AND/OR types a free-text note.
 * Both are concatenated into the final rejection reason sent to the API.
 */
import { useState, useEffect } from 'react';
import { XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useLanguage } from '../../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentRejectModal({
  open,
  onOpenChange,
  selectedAgentRequest,
  agentRejectReason,
  setAgentRejectReason,
  onCancel,
  onConfirm,
  processingAgent,
}) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [loadingReasons, setLoadingReasons] = useState(false);

  // Fetch the active agency_rejection reasons every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setSelectedReasonId('');
    setLoadingReasons(true);
    axios
      .get(`${BACKEND_URL}/api/dropdown-reasons/agency_rejection`)
      .then((res) => setReasons(res.data?.data || []))
      .catch(() => setReasons([]))
      .finally(() => setLoadingReasons(false));
  }, [open]);

  const handlePickReason = (rid) => {
    setSelectedReasonId(rid);
    const r = reasons.find((x) => x.id === rid);
    if (!r) return;
    const label = isAr ? (r.label_ar || r.label_en) : (r.label_en || r.label_ar);
    // If a note already exists, append the picked reason; otherwise replace.
    const existing = (agentRejectReason || '').trim();
    if (!existing || reasons.some((x) => existing.startsWith(isAr ? x.label_ar : x.label_en))) {
      setAgentRejectReason(label);
    } else {
      setAgentRejectReason(`${label} — ${existing}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md"
        data-testid="agent-reject-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2 text-red-400">
            <XCircle className="h-6 w-6" />
            {isAr ? 'رفض طلب الوكالة' : 'Reject Agency Request'}
          </DialogTitle>
          <DialogDescription className="text-[#808080]">
            {isAr ? 'سيتم رفض طلب: ' : 'Rejecting request from: '}
            <span className="text-white">{selectedAgentRequest?.user?.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Reason dropdown */}
          <div>
            <label className="text-sm text-[#808080] mb-2 block">
              {isAr ? 'سبب الرفض (من القائمة)' : 'Rejection reason (from list)'}
            </label>
            <Select
              value={selectedReasonId}
              onValueChange={handlePickReason}
              disabled={loadingReasons || reasons.length === 0}
            >
              <SelectTrigger
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                data-testid="agent-reject-reason-select"
              >
                <SelectValue
                  placeholder={
                    loadingReasons
                      ? (isAr ? 'جاري التحميل…' : 'Loading…')
                      : reasons.length === 0
                      ? (isAr ? 'لا توجد أسباب مُعدّة' : 'No reasons configured')
                      : (isAr ? 'اختر سبباً' : 'Pick a reason')
                  }
                />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white">
                {reasons.map((r) => (
                  <SelectItem
                    key={r.id}
                    value={r.id}
                    data-testid={`agent-reject-reason-option-${r.id}`}
                  >
                    {isAr ? (r.label_ar || r.label_en) : (r.label_en || r.label_ar)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#606060] mt-1">
              {isAr
                ? 'الأسباب تُدار من: لوحة التحكم → CLOUDGM FZ LLC → التحكم والمعلومات بمنصة التجار → القائمة المنسدلة'
                : 'Reasons managed in: Control Panel → CLOUDGM FZ LLC → Merchants Platform Control → Store Request Dropdowns'}
            </p>
          </div>

          {/* Free-text note */}
          <div>
            <label className="text-sm text-[#808080] mb-2 block">
              {isAr ? 'ملاحظات إضافية (اختياري)' : 'Additional notes (optional)'}
            </label>
            <textarea
              value={agentRejectReason}
              onChange={(e) => setAgentRejectReason(e.target.value)}
              placeholder={isAr ? 'اكتب أي توضيح أو سبب إضافي…' : 'Write any clarification or extra context…'}
              className="w-full h-24 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-3 text-white placeholder-[#606060] resize-none focus:outline-none focus:border-red-500"
              data-testid="agent-reject-reason"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
            data-testid="agent-reject-cancel-btn"
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={processingAgent === selectedAgentRequest?.id}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="agent-reject-confirm-btn"
          >
            {processingAgent === selectedAgentRequest?.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              isAr ? 'تأكيد الرفض' : 'Confirm Reject'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
